import { ReactElement, Fragment } from "react";
import { infoTag } from "@/lib/tags";

type Content = Array<ReactElement>;

function parse(string: string): Content {
    const lines = string.split("\n");

    const res: Content = [];
    for (const line of lines) {
        if (line.startsWith(infoTag)) {
            continue;
        }
        res.push(parseLine(lineFilter(line)));
    }
    return res;
}

function lineFilter(line: string): string {
    // lazily (as opposed to greedily) remove html-like tags
    return line.trim().replace(/<.*?>/g, "");
}

function isUrlNaive(urlString: string): boolean {
    return (
        (urlString.startsWith("http://") ||
            urlString.startsWith("https://") ||
            urlString.startsWith("www.") ||
            urlString.includes(".com") ||
            urlString.includes(".org") ||
            urlString.includes(".net") ||
            urlString.includes(".at") ||
            urlString.includes(".de") ||
            urlString.includes(".dk") ||
            urlString.includes(".fi") ||
            urlString.includes(".fr") ||
            urlString.includes(".nl") ||
            urlString.includes(".se")) &&
        !urlString.includes("@") &&
        !!toAbsolute(urlString)
    );
}

function isEmailNaive(email: string): boolean {
    return email.includes("@") && email.includes(".") && email.length > 5;
}

function toAbsolute(urlString: string): string | undefined {
    if (URL.canParse(urlString)) {
        return urlString;
    } else if (URL.canParse("https://" + urlString)) {
        return "https://" + urlString;
    }
    return undefined;
}

function parseLine(line: string): ReactElement {
    const res: ReactElement[] = [];
    
    // First handle markdown links
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;
    
    while ((match = markdownLinkRegex.exec(line)) !== null) {
        // Add text before the link
        if (match.index > lastIndex) {
            const beforeText = line.slice(lastIndex, match.index);
            res.push(<Fragment key={`text-${lastIndex}`}>{parsePlainText(beforeText)}</Fragment>);
        }
        
        // Add the link
        const url = toAbsolute(match[2]);
        if (url) {
            const isInternal = url.includes("dpj.se") || 
                             url.includes("dpj-workspace.com") || 
                             url.includes("localhost");
            
            res.push(
                <a
                    key={`link-${match.index}`}
                    className="!tw-text-dpj-forest-green !tw-underline hover:tw-underline-offset-4 focus:hover:tw-underline-offset-4"
                    target={isInternal ? "_parent" : "_blank"}
                    data-type={isInternal ? "url-internal" : "url-external"}
                    href={url}
                >
                    {match[1]}
                </a>
            );
        } else {
            res.push(<Fragment key={`invalid-${match.index}`}>{match[0]}</Fragment>);
        }
        
        lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after the last link
    if (lastIndex < line.length) {
        res.push(<Fragment key={`text-${lastIndex}`}>{parsePlainText(line.slice(lastIndex))}</Fragment>);
    }
    
    return <>{res}</>;
}

function parsePlainText(text: string): ReactElement {
    const words = text.split(" ");
    return (
        <>
            {words.map((w, i) => {
                let el = <Fragment key={`word-${i}`}>{w}</Fragment>;
                
                if (isUrlNaive(w)) {
                    const url = toAbsolute(w);
                    if (url) {
                        const isInternal = url.includes("dpj.se") || 
                                         url.includes("dpj-workspace.com") || 
                                         url.includes("localhost");
                        
                        el = (
                            <a
                                key={`url-${i}`}
                                className="!tw-text-dpj-forest-green !tw-underline hover:tw-underline-offset-4 focus:hover:tw-underline-offset-4"
                                target={isInternal ? "_parent" : "_blank"}
                                data-type={isInternal ? "url-internal" : "url-external"}
                                href={url}
                            >
                                {w}
                            </a>
                        );
                    }
                } else if (isEmailNaive(w)) {
                    el = (
                        <a
                            key={`email-${i}`}
                            className="!tw-text-dpj-forest-green !tw-underline hover:tw-underline-offset-4 focus:hover:tw-underline-offset-4"
                            href={`mailto:${w}`}
                            data-type="email"
                        >
                            {w}
                        </a>
                    );
                }
                
                return (
                    <Fragment key={`word-wrapper-${i}`}>
                        {i > 0 && " "}
                        {el}
                    </Fragment>
                );
            })}
        </>
    );
}

type Props = {
    str: string;
    children?: ReactElement;
};

function Content({ str, children }: Props): JSX.Element {
    const parsed = parse(str);
    return (
        <>
            {children}
            {parsed.map((s, i) => (
                <Fragment key={`line-${i}`}>
                    {i > 0 && <br />}
                    {s}
                </Fragment>
            ))}
        </>
    );
}

export { Content };
